import { Link } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { Home, BookOpen, Briefcase, Users, FileText, Sun, Moon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Toggle } from '@/components/ui/toggle';
import { Separator } from '@/components/ui/separator';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { useSidebarOptional } from '@/lib/sidebar-utils';
import { NotificationBell } from '@/components/ui/feedback/notification-bell';
import { UserMenu } from './user-menu';
import { useUser } from '@/lib/auth';
import { useIsMobile } from '@/hooks/use-mobile';
import { ROUTES } from '@/config/routes';
import propelLogo from '@/assets/propel-logo.png';

interface NavLinkProps {
  to: string;
  icon: React.ElementType;
  label: string;
  ariaLabel?: string;
}

const NavLink = ({ to, icon: Icon, label, ariaLabel }: NavLinkProps) => (
  <Link
    to={to}
    className="flex items-center gap-2 rounded-lg px-3 py-2 font-medium text-foreground transition-colors duration-fast hover:bg-muted hover:text-primary"
    aria-label={ariaLabel || label}
  >
    <Icon className="size-4" />
    <span>{label}</span>
  </Link>
);

/**
 * AppHeader - Main application header component
 *
 * Unauthenticated state:
 * - Logo (links to home) | Home, Learning Hub, For Employers, Company Directory, Resources | Sign In, Sign Up
 *
 * Authenticated state (requires BOTH user AND session):
 * - Logo (links to dashboard) | Theme Toggle, Notifications, User Menu
 * - All feature navigation has moved to the sidebar
 *
 * CRITICAL: When email confirmation is enabled, registerFn returns user but session=null.
 * We must check BOTH user AND session to determine if user is truly authenticated.
 */
export const AppHeader = () => {
  const { theme, setTheme } = useTheme();
  const authResult = useUser();
  const auth = authResult.data;
  const user = auth?.user ?? null;
  const session = auth?.session ?? null;
  const isLoading = authResult.isLoading;
  const isMobile = useIsMobile();

  // Check if we're inside a SidebarProvider (AppLayout vs PublicLayout)
  const sidebarContext = useSidebarOptional();
  const hasSidebarContext = sidebarContext !== null;

  // User is only considered authenticated when both user AND session exist
  const isAuthenticated = !!(user && session);

  return (
    <nav className="px-4 py-3 md:px-6 md:py-4 lg:px-8">
      <div className="container-max">
        <div className="flex items-center justify-between gap-4">
          {/* Left: Logo - links to dashboard for authenticated users, home for unauthenticated */}
          <Link
            to={isAuthenticated ? ROUTES.DASHBOARD : ROUTES.HOME}
            className="group flex items-center gap-3"
            aria-label={isAuthenticated ? "Propel Pathways - Go to dashboard" : "Propel Pathways - Go to home"}
          >
            <div className="flex size-10 items-center justify-center overflow-hidden rounded-lg bg-muted transition-transform duration-fast group-hover:scale-105">
              <img
                src={propelLogo}
                alt="Propel Pathways Logo"
                className="size-8 object-contain"
                loading="eager"
                decoding="async"
                fetchPriority="high"
              />
            </div>
            <span className="text-headline hidden text-xl font-bold text-primary sm:block">
              Propel Pathways
            </span>
          </Link>

          {/* Mobile: Sidebar Trigger (only when authenticated + mobile + inside SidebarProvider) */}
          {isAuthenticated && isMobile && hasSidebarContext && (
            <SidebarTrigger className="md:hidden" />
          )}

          {/* Middle: Navigation (hidden on mobile, only for unauthenticated users) */}
          {/* Authenticated users have sidebar navigation - no header nav needed */}
          {!isAuthenticated && (
            <div className="hidden flex-1 md:block">
              {isLoading ? (
                <div className="flex items-center gap-6">
                  <div className="h-9 w-24 animate-pulse rounded-lg bg-muted" />
                  <div className="h-9 w-24 animate-pulse rounded-lg bg-muted" />
                </div>
              ) : (
                /* Unauthenticated Navigation */
                <div className="flex items-center gap-2">
                  <NavLink
                    to={ROUTES.HOME}
                    icon={Home}
                    label="Home"
                    ariaLabel="Go to home page"
                  />
                  <NavLink
                    to={ROUTES.PUBLIC_LEARNING_HUB}
                    icon={BookOpen}
                    label="Learning Hub"
                    ariaLabel="Browse workshops"
                  />
                  <NavLink
                    to={ROUTES.FOR_EMPLOYERS}
                    icon={Briefcase}
                    label="For Employers"
                    ariaLabel="Information for employers"
                  />
                  <NavLink
                    to={ROUTES.NETWORK}
                    icon={Users}
                    label="Company Directory"
                    ariaLabel="Browse company directory"
                  />
                  <NavLink
                    to={ROUTES.PUBLIC_RESOURCE_LIBRARY}
                    icon={FileText}
                    label="Resource Library"
                    ariaLabel="Browse resource library"
                  />
                </div>
              )}
            </div>
          )}

          {/* Right: User Actions */}
          <div className="flex items-center gap-2">
            {/* Theme Toggle - Available to ALL users (authenticated and unauthenticated) */}
            <Toggle
              pressed={theme === 'dark'}
              onPressedChange={(pressed) => setTheme(pressed ? 'dark' : 'light')}
              aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
              className="size-10"
            >
              {theme === 'dark' ? (
                <Sun className="size-5" />
              ) : (
                <Moon className="size-5" />
              )}
            </Toggle>

            {isLoading ? (
              <div className="size-10 animate-pulse rounded-full bg-muted" />
            ) : isAuthenticated ? (
              /* Authenticated Actions */
              <>
                {/* Notification Bell */}
                <NotificationBell
                  notifications={[]}
                  className="hidden sm:flex"
                />

                {/* User Menu */}
                <UserMenu />
              </>
            ) : (
              /* Unauthenticated Actions */
              <>
                <Separator orientation="vertical" className="mx-2 hidden h-6 sm:block" />
                <Button asChild aria-label="Sign in to your account">
                  <Link to={ROUTES.SIGN_IN}>Sign In</Link>
                </Button>
                <Button asChild variant="outline" aria-label="Create a new account">
                  <Link to={ROUTES.ACCOUNT_TYPE}>Sign Up</Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

